import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useTranslation } from "react-i18next";
import { getInitials } from "../../../utils/utils";
import { focusClientComposer } from "../../../utils/focusClientComposer";
import {
  buildQuotePresentation,
  formatCatalogPageUrl,
  hasQuoteContext,
  hasVehicleInfo,
  parsePartsQuoteRequest,
  parseQuoteContext,
} from "../../../utils/parsePartsQuoteRequest";

const parseDisplayDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (mysqlRegex.test(trimmed)) {
      const [datePart, timePart] = trimmed.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year} · ${timePart.slice(0, 5)}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const copyText = (value) => {
  if (!value || !navigator.clipboard?.writeText) return;
  navigator.clipboard.writeText(String(value)).catch(() => {});
};

const PartRow = ({ item, prominent = false, onCopySku }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 1.25,
        alignItems: "center",
        px: prominent ? 1.5 : 1.25,
        py: prominent ? 1.25 : 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          minWidth: prominent ? 48 : 40,
          height: prominent ? 48 : 40,
          px: 0.5,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.08),
          color: "primary.main",
          fontWeight: 800,
          fontSize: prominent ? "0.95rem" : "0.82rem",
          lineHeight: 1,
        }}
      >
        ×{item.qty}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontWeight: 800,
            fontSize: prominent ? "1.05rem" : "0.92rem",
            letterSpacing: 0.3,
            lineHeight: 1.2,
          }}
        >
          {item.sku}
        </Typography>
        {item.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, lineHeight: 1.35 }}
          >
            {item.description}
          </Typography>
        )}
      </Box>
      <Tooltip title={t("messages.partsQuoteCopySku", "Copiar código")}>
        <IconButton size="small" onClick={() => onCopySku(item.sku)} sx={{ color: "text.secondary" }}>
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ClientStrip = ({ presentation, senderName, compact = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const { primaryName, companyLine, email } = presentation;
  const avatarName = primaryName || senderName || "?";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: compact ? 0 : 0.25,
      }}
    >
      <Avatar
        sx={{
          width: compact ? 30 : 36,
          height: compact ? 30 : 36,
          fontSize: compact ? "0.72rem" : "0.82rem",
          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.18 : 0.08),
          color: "text.primary",
          fontWeight: 700,
        }}
      >
        {getInitials(avatarName)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {primaryName && (
          <Typography variant="body2" fontWeight={700} noWrap>
            {primaryName}
          </Typography>
        )}
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          {companyLine && (
            <Typography variant="caption" color="text.secondary">
              {companyLine}
            </Typography>
          )}
          {companyLine && email && (
            <Typography variant="caption" color="text.disabled">
              ·
            </Typography>
          )}
          {email && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                {email}
              </Typography>
              <Tooltip title={t("messages.partsQuoteCopyEmail", "Copiar e-mail")}>
                <IconButton size="small" onClick={() => copyText(email)} sx={{ color: "text.secondary", p: 0.25 }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const ContextChips = ({ entries, max, overflowLabel }) => {
  if (!entries?.length) return null;
  const visible = max ? entries.slice(0, max) : entries;
  const hidden = max ? Math.max(0, entries.length - max) : 0;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
      {visible.map((entry) => (
        <Chip
          key={entry.key}
          size="small"
          variant="outlined"
          label={`${entry.label}: ${entry.value}`}
          sx={{ height: 24, maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
        />
      ))}
      {hidden > 0 && (
        <Chip size="small" variant="outlined" label={overflowLabel || `+${hidden}`} sx={{ height: 24 }} />
      )}
    </Stack>
  );
};

const VehicleSummary = ({ vehicle, compact = false }) => {
  const { t } = useTranslation("chat");
  if (!hasVehicleInfo(vehicle)) return null;

  const parts = [
    vehicle.vinOrPlates && `${t("messages.partsQuoteVinPlate", "VIN/Placa")}: ${vehicle.vinOrPlates}`,
    vehicle.seriesModel && `${t("messages.partsQuoteSeriesModel", "Série/Modelo")}: ${vehicle.seriesModel}`,
    vehicle.year && `${t("messages.partsQuoteYear", "Ano")}: ${vehicle.year}`,
  ].filter(Boolean);

  return (
    <Typography variant={compact ? "caption" : "body2"} color="text.secondary" sx={{ lineHeight: 1.45 }}>
      <Typography component="span" variant="inherit" fontWeight={700} color="text.secondary">
        {t("messages.partsQuoteVehicle", "Veículo")}:{" "}
      </Typography>
      {parts.join(" · ")}
    </Typography>
  );
};

const ContextSection = ({ contextParsed, pageMeta }) => {
  const { t } = useTranslation("chat");
  if (!hasQuoteContext(contextParsed) && !pageMeta) return null;

  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 0.75, letterSpacing: 0.8 }}
      >
        {t("messages.partsQuoteContextSection", "Contexto da busca")}
      </Typography>
      {hasQuoteContext(contextParsed) && <ContextChips entries={contextParsed.entries} />}
      {pageMeta && (
        <Link
          href={pageMeta.full}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ display: "block", mt: hasQuoteContext(contextParsed) ? 0.75 : 0, wordBreak: "break-all" }}
        >
          {pageMeta.label}
        </Link>
      )}
    </Box>
  );
};

const QuoteModal = ({ open, onClose, quote, presentation, senderName, contextParsed, pageMeta }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const vehicleVisible = hasVehicleInfo(quote.vehicle);

  const handleReplyToClient = () => {
    onClose();
    focusClientComposer();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Inventory2OutlinedIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              {t("messages.partsQuoteModalTitle", "Orçamento de peças")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("messages.partsQuoteModalSubtitle", "Solicitação via catálogo")}
            </Typography>
          </Box>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={t("messages.partsQuoteItemCount", "{{count}} peça(s)", {
              count: presentation.itemCount,
            })}
          />
        </Stack>
        <IconButton
          aria-label={t("messages.noteCloseFull", "Fechar")}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
        <Stack spacing={2.25}>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mb: 1, letterSpacing: 0.8 }}
            >
              {t("messages.partsQuoteItemsLabel", "Peças solicitadas")}
            </Typography>
            <Stack spacing={1}>
              {quote.items.map((item, index) => (
                <PartRow key={`${item.sku}-${index}`} item={item} onCopySku={copyText} />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.03),
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mb: 1, letterSpacing: 0.8 }}
            >
              {t("messages.partsQuoteClientSection", "Cliente")}
            </Typography>
            <ClientStrip presentation={presentation} senderName={senderName} />
            {presentation.showContactSeparately && presentation.contact && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <Typography component="span" variant="caption" color="text.secondary" fontWeight={700}>
                  {t("messages.partsQuoteContact", "Responsável")}:{" "}
                </Typography>
                {presentation.contact}
              </Typography>
            )}
          </Box>

          {vehicleVisible && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, letterSpacing: 0.8 }}
              >
                {t("messages.partsQuoteVehicleSection", "Dados do veículo")}
              </Typography>
              <VehicleSummary vehicle={quote.vehicle} />
            </Box>
          )}

          {quote.notes && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.08),
                borderLeft: `3px solid ${theme.palette.warning.main}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                {t("messages.partsQuoteNotes", "Observações do cliente")}
              </Typography>
              <Typography variant="body2">{quote.notes}</Typography>
            </Box>
          )}

          <ContextSection contextParsed={contextParsed} pageMeta={pageMeta} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
          {t("messages.partsQuoteReplyHint", "Responda ao cliente pelo campo de mensagem abaixo.")}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            disableElevation
            startIcon={<ReplyOutlinedIcon />}
            onClick={handleReplyToClient}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("messages.partsQuoteReplyClient", "Responder ao cliente")}
          </Button>
          {pageMeta?.full && (
            <Button
              component="a"
              href={pageMeta.full}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("messages.partsQuoteOpenPage", "Abrir catálogo")}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const PartsQuoteRequestMessage = ({ message, displayName }) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const [dialogOpen, setDialogOpen] = useState(false);

  const quote = useMemo(() => parsePartsQuoteRequest(message?.text), [message?.text]);
  const contextParsed = useMemo(() => parseQuoteContext(quote?.context), [quote?.context]);
  const pageMeta = useMemo(() => formatCatalogPageUrl(quote?.page), [quote?.page]);

  if (!quote) return null;

  const senderName = displayName || message?.senderName || t("messages.clientFallback", "Cliente");
  const presentation = buildQuotePresentation({ quote, senderName });
  const displayDate = parseDisplayDate(
    message?.createdAt || message?.updatedAt || message?.lastUpdate || message?.timestamp
  );

  const previewItems = quote.items.slice(0, 2);
  const hiddenItems = Math.max(0, quote.items.length - previewItems.length);
  const vehicleVisible = hasVehicleInfo(quote.vehicle);
  const cardBorder = theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider;
  const contextOverflowLabel = t("messages.partsQuoteContextMore", "+{{count}} filtro(s)", {
    count: Math.max(0, contextParsed.entries.length - 3),
  });

  return (
    <>
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Box
          sx={{
            borderRadius: 2.5,
            overflow: "hidden",
            border: `1px solid ${cardBorder}`,
            bgcolor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === "dark"
                ? "none"
                : "0 2px 10px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.05)} 0%, transparent 100%)`,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.1),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={800} lineHeight={1.25}>
                {t("messages.partsQuoteCardTitle", "Orçamento de peças")}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                <Stack direction="row" spacing={0.35} alignItems="center">
                  <StorefrontOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    {t("messages.partsQuoteSourceCatalog", "Catálogo")}
                  </Typography>
                </Stack>
                {displayDate && (
                  <>
                    <Typography variant="caption" color="text.disabled">
                      ·
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {displayDate}
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
            <Chip
              size="small"
              label={t("messages.partsQuoteItemCountShort", "{{count}} peça(s)", {
                count: presentation.itemCount,
              })}
              sx={{
                height: 24,
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
                color: "primary.main",
              }}
            />
          </Box>

          <Box sx={{ px: 2, py: 1.5 }}>
            <Stack spacing={1}>
              {previewItems.map((item, index) => (
                <PartRow key={`${item.sku}-${index}`} item={item} prominent onCopySku={copyText} />
              ))}
            </Stack>
            {hiddenItems > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {t("messages.partsQuoteMoreItems", "+{{count}} peça(s) nesta solicitação", { count: hiddenItems })}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.04 : 0.02),
            }}
          >
            <ClientStrip presentation={presentation} senderName={senderName} compact />
          </Box>

          {vehicleVisible && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.03 : 0.015),
              }}
            >
              <VehicleSummary vehicle={quote.vehicle} compact />
            </Box>
          )}

          {quote.notes && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.25}>
                {t("messages.partsQuoteNotes", "Observações do cliente")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {quote.notes}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {hasQuoteContext(contextParsed) && (
              <ContextChips entries={contextParsed.entries} max={3} overflowLabel={contextOverflowLabel} />
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              {pageMeta?.full && (
                <Button
                  component="a"
                  href={pageMeta.full}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
                >
                  {t("messages.partsQuoteOpenCatalogShort", "Catálogo")}
                </Button>
              )}
              <Button
                size="small"
                onClick={() => setDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
              >
                {t("messages.partsQuoteExpandFull", "Detalhes")}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>

      <QuoteModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        quote={quote}
        presentation={presentation}
        senderName={senderName}
        contextParsed={contextParsed}
        pageMeta={pageMeta}
      />
    </>
  );
};

export default PartsQuoteRequestMessage;
